import { NgClass, NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, Inject, Optional } from '@angular/core';
import { Client } from '@df/core/service/client';
import { ZippyModule } from '@df/legacy/ui/zippy/zippy.module';
import {
    AdvancedCommerceConfigStore,
    AdvancedCommerceListingFiltersComponentDef,
    AdvancedCommerceStore
} from '@df/module-advanced-commerce';
import { DfModal } from '@df/ui/modal/df-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { ModalModule } from '@df/ui/modal/modal.module';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import flatten from 'lodash-es/flatten';
import { NListing } from 'projects/app-default/src/app/catalog/ui/listing/product-listing.interface';
import { ModalLayoutRightComponent, NModalLayoutRight } from '../../../../ui/modal/modal-layout-right.component';
import { IAcFilterSelected, IAcFilterSelectedOption, IAcFilterSelectedRange } from '../advanced-commerce-product-listing-tools.component';
import { AdvancedCommerceProductListingSortComponent } from '../sort/advanced-commerce-advanced-commerce-product-listing-sort.component';
import { AdvancedCommerceFilterAttributeOptionComponent } from './advanced-commerce-filter-attribute-option.component';
import { AdvancedCommerceFilterAttributeRangeComponent } from './advanced-commerce-filter-attribute-range.component';
import { FilterAttributeRange } from '@df/catalog-ui/listing/filter/filter-attribute-range';
import { LocalProduct } from 'projects/app-default/src/app/catalog/model/product/product.decorator';
import { IProductDataV2 } from '@df/catalog/product/product';

export namespace NAdvancedCommerceListingFiltersModal {
    /**
     * Modal identifier
     */
    export const id = 'ac-product-listing-filters';

    /**
     * Data passed to modal
     */
    export interface Data {}
}
@DfModal({
    id: NAdvancedCommerceListingFiltersModal.id,
    name: 'Advanced Commerce Listing Filters Popup',
    position: 'right',
    config: sizeName => ({
        ...NModalLayoutRight.ModalConfig,
        width: { S: '355px', M: '500px', L: '37.5rem', X: '37.5rem' }[sizeName],
        maxWidth: { S: '355px', M: '500px' }[sizeName],
        height: '100%'
    }),
    reopenPolicy: NDfModal.ReopenPolicy.first,
    sizeRestriction: sizeName => ({ S: ['S'], M: ['M'], L: ['L', 'X'], X: ['L', 'X'] })[sizeName]
})
@Component({
    selector: 'ac-filter-modal',
    templateUrl: '../../../../catalog/ui/listing/filter/filter-modal.html',
    standalone: true,
    imports: [ModalLayoutRightComponent, TranslationModule, ZippyModule, NgComponentOutlet, ModalModule, NgClass, PriceComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdvancedCommerceListingFiltersModalComponent
    extends AdvancedCommerceListingFiltersComponentDef
    implements NDfModal.IModalComponent, NListing.IFilter
{
    protected enabledAc = !!inject(AdvancedCommerceConfigStore)?.config()?.enabled;
    /**
     * Modal option config
     */
    modalConfig!: NDfModal.IConfig;

    constructor(
        readonly client: Client,
        @Optional()
        protected modalRef: ModalRef<AdvancedCommerceListingFiltersModalComponent>,
        @Optional()
        @Inject(MODAL_DATA)
        public data: NAdvancedCommerceListingFiltersModal.Data
    ) {
        super();
    }

    getFilterOptions = computed(() => {
        return [...this.store.filtersEntities()].filter(
            filter => filter.type === 'range' || filter.options?.some(option => option.count && option.count > 0)
        );
    });

    get activeFiltersCount(): number | undefined {
        const filters = this.getFilterOptions();
        let count = filters?.filter(filter => filter?.options?.some(option => option.selected === true)).length || 0;

        const priceFilter = filters?.find(f => f.id === 'price');
        if (priceFilter && (this.store.filtersPriceFrom() || this.store.filtersPriceTo())) {
            const currentFrom = this.store.filtersPriceFrom() || priceFilter.meta?.min || 0;
            const currentTo = this.store.filtersPriceTo() || priceFilter.meta?.max || 0;
            const defaultMin = priceFilter.meta?.min || 0;
            const defaultMax = priceFilter.meta?.max || 0;

            if (currentFrom !== defaultMin || currentTo !== defaultMax) {
                count++;
            }
        }

        return count;
    }
    close() {
        this.modalRef.close();
    }
    clearFilters(): void {
        this.store.filtersResetSelection();
    }

    get sortSelectedLabel(): string | undefined {
        return this.store
            .sortingEntities()
            ?.find(
                o =>
                    o.code ===
                    (Array.isArray(this.store.queryParamSort())
                        ? this.store.queryParamSort()?.[0]
                        : this.store.queryParamSort() || 'default')
            )?.label;
    }

    get sortOutlet(): NListing.IOutlet {
        return { component: AdvancedCommerceProductListingSortComponent, inputs: { displayAs: 'buttons' } };
    }

    getFilterOutlet(filter: AdvancedCommerceStore.Filters.Filter): NListing.IOutlet {
        return filter.id === 'price'
            ? { component: AdvancedCommerceFilterAttributeRangeComponent, inputs: { filter: filter } }
            : {
                  component: AdvancedCommerceFilterAttributeOptionComponent,
                  inputs: { filter: filter }
              };
    }

    protected get filtersWithSelection(): IAcFilterSelected[] | undefined {
        const optionFilters = flatten(
            this.store.filtersWithSelection().map(filter =>
                filter.options
                    ?.filter(option => option.selected)
                    .map(
                        option =>
                            ({
                                filterType: 'ac-filter',
                                label: filter.label,
                                value: option.label,
                                matchesCount: option.count || 0,
                                unselect: (filter: AdvancedCommerceStore.Filters.Filter, option: AdvancedCommerceStore.Filters.Option) => {
                                    this.store.filtersOptionUnselect(filter, option);
                                },
                                filterProp: filter,
                                optionProp: option
                            }) as IAcFilterSelectedOption
                    )
            )
        ).filter(Boolean) as IAcFilterSelectedOption[];

        const rangeFilters: any[] = [];
        const priceFilter = this.getFilterOptions().find(f => f.id === 'price');
        if (priceFilter && (this.store.filtersPriceFrom() || this.store.filtersPriceTo())) {
            const currentFrom = this.store.filtersPriceFrom() || priceFilter.meta?.min || 0;
            const currentTo = this.store.filtersPriceTo() || priceFilter.meta?.max || 0;
            const defaultMin = priceFilter.meta?.min || 0;
            const defaultMax = priceFilter.meta?.max || 0;

            if (currentFrom !== defaultMin || currentTo !== defaultMax) {
                rangeFilters.push({
                    filterType: 'ac-filter-range',
                    label: priceFilter.label,
                    from: currentFrom,
                    to: currentTo,
                    unselect: () => {
                        this.store.filtersPriceFromSet(priceFilter, defaultMin);
                        this.store.filtersPriceToSet(priceFilter, defaultMax);
                    },
                    filterProp: priceFilter
                });
            }
        }

        return [...optionFilters, ...rangeFilters];
    }
    protected asRangeFilter(filter): FilterAttributeRange<LocalProduct<IProductDataV2>> {
        return filter as any as FilterAttributeRange<LocalProduct<IProductDataV2>>;
    }
    protected asAcFilter(filter): IAcFilterSelectedOption {
        return filter as any as IAcFilterSelectedOption;
    }
    protected asAcFilterRange(filter): IAcFilterSelectedRange {
        return filter as any as IAcFilterSelectedRange;
    }
}
